import { Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from '@/components/layout/molecules/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from '@/components/layout/molecules/ErrorBoundary';
import { router } from '@/components/features/routing/Routes';
import { Spinner } from '@/components/ui/atoms/spinner';
import { AppContextProvider } from '@/components/layout/AppContextProvider';
import { useAuth } from '@/lib/hooks/useAuth';
import { useWwwRedirect } from '@/hooks/useWwwRedirect';
import { Analytics } from '@vercel/analytics/react';
import { performMetaVersionHandshake } from '@/lib/services/metaVersion';
import { detectDatabaseReset } from '@/lib/services/developmentResetDetection';
import {
  initializeResetChannel,
  cleanupResetChannel,
  initiateAtomicReset,
} from '@/lib/services/atomicReset';
import { orchestrateCacheInvalidation } from '@/lib/services/cacheInvalidationOrchestrator';
import { useBrandingCacheResetListener } from '@/hooks/useBrandingCacheInvalidation';
import { CacheErrorBoundary } from '@/components/features/errors/CacheInvalidationErrorBoundary';
import { useStorageInitialization } from '@/hooks/useStorageInitialization';
import { initializeCacheMonitoring } from '@/lib/services/cacheMonitoringService';
import { ModalProvider } from '@/lib/contexts/ModalContext';
import { ThemeProvider } from '@/hooks/useTheme';

// Simple component to ensure useAuth runs at the root with performance tracking
function AuthInitializer() {
  // Set a performance mark for auth initialization
  useEffect(() => {
    performance.mark('auth-initialization-start');

    return () => {
      // Optional cleanup if needed
      if (
        performance.getEntriesByName('auth-initialization-start').length > 0
      ) {
        performance.measure('auth-initialization', 'auth-initialization-start');
        // Performance metric logged - can be sent to analytics if needed
      }
    };
  }, []);

  useAuth();
  return null;
}

// Cache invalidation initializer - performs meta-version handshake at app boot
function CacheInvalidationInitializer() {
  const [isHandshakeComplete, setIsHandshakeComplete] = useState(false);
  const queryClient = useQueryClient(); // Access to React Query client

  // Phase 3: Initialize branding cache reset listener
  useBrandingCacheResetListener();

  // Phase 4: Initialize advanced storage system
  const {
    isInitialized: storageReady,
    isLoading: storageLoading,
    hasError: storageError,
    migrationResult,
  } = useStorageInitialization();

  // Initialize BroadcastChannel for cross-tab coordination
  useEffect(() => {
    initializeResetChannel();

    return () => {
      cleanupResetChannel();
    };
  }, []);

  // Phase 5: Initialize monitoring system
  useEffect(() => {
    initializeCacheMonitoring();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const performHandshake = async () => {
      try {
        performance.mark('meta-version-handshake-start');

        // First, check for development database resets
        const resetDetected = await detectDatabaseReset();
        if (resetDetected) {
          console.log(
            'App: Database reset detected - meta-version will be updated'
          );
        }

        const result = await performMetaVersionHandshake();

        if (!isMounted) return;

        // Log handshake result for debugging
        console.log('App: Meta-version handshake complete', {
          version: result.version,
          hasChanged: result.hasChanged,
          previousVersion: result.previousVersion,
        });

        // Phase 2: Trigger cache invalidation when database changes are detected
        if (result.hasChanged) {
          console.log(
            'App: Database change detected - triggering atomic cache reset'
          );

          try {
            // Initiate atomic reset with cache invalidation
            const resetReason = resetDetected
              ? 'Database reset detected'
              : 'Meta-version change detected';

            await initiateAtomicReset(result.version, resetReason);

            // Orchestrate cache invalidation
            const invalidationResult = await orchestrateCacheInvalidation(
              queryClient,
              resetReason
            );

            console.log(
              'App: Cache invalidation completed',
              invalidationResult
            );
          } catch (error) {
            console.error('App: Cache invalidation failed:', error);
          }
        }

        setIsHandshakeComplete(true);

        // Performance measurement
        if (
          performance.getEntriesByName('meta-version-handshake-start').length >
          0
        ) {
          performance.measure(
            'meta-version-handshake',
            'meta-version-handshake-start'
          );
          const measurement = performance.getEntriesByName(
            'meta-version-handshake'
          )[0];
          console.log(
            'Meta-version handshake performance:',
            measurement?.duration.toFixed(2),
            'ms'
          );
        }
      } catch (error) {
        console.error('App: Meta-version handshake failed:', error);
        // Continue app initialization even if handshake fails
        if (isMounted) {
          setIsHandshakeComplete(true);
        }
      }
    };

    performHandshake();

    return () => {
      isMounted = false;
    };
  }, []);

  // Block app initialization until handshake and storage are ready
  // This ensures we detect database changes before any cache access
  if (!isHandshakeComplete || storageLoading) {
    const loadingMessage = !storageReady
      ? 'Initializing storage system...'
      : 'Initializing cache system...';

    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">{loadingMessage}</p>
          {migrationResult && migrationResult.migrated.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Migrated {migrationResult.migrated.length} storage keys
            </p>
          )}
        </div>
      </div>
    );
  }

  // Handle storage initialization errors
  if (storageError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <p className="text-sm text-gray-600">
            Storage system initialization failed
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200"
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }

  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Wrapper component for React Router
function AppRoutes() {
  return (
    <RouterProvider
      router={router}
      fallbackElement={
        <div className="min-h-screen flex items-center justify-center bg-brand-background">
          <Spinner size="lg" />
        </div>
      }
    />
  );
}

export default function App() {
  // Redirect from www to bare domain for consistent auth cookies
  useWwwRedirect();

  // Domain consistency effect - ensure we always use localhost instead of 127.0.0.1
  useEffect(() => {
    performance.mark('app-initialization-start');

    if (window.location.hostname === '127.0.0.1') {
      console.log(
        'App: Redirecting from 127.0.0.1 to localhost for consistent auth storage'
      );
      const currentUrl = window.location.href;
      const newUrl = currentUrl.replace('127.0.0.1', 'localhost');
      window.location.replace(newUrl);
      return; // Early return as we're redirecting
    }

    // Measure initialization time for performance monitoring
    return () => {
      if (performance.getEntriesByName('app-initialization-start').length > 0) {
        performance.measure('app-initialization', 'app-initialization-start');
        console.log(
          'App initialization performance:',
          performance
            .getEntriesByName('app-initialization')[0]
            ?.duration.toFixed(2),
          'ms'
        );
      }
    };
  }, []);

  // Handle Supabase auth redirects at app root level
  useEffect(() => {
    // Only run this effect once on mount (root-level handling)
    // Skip if we're redirecting for domain consistency

    // Log the current URL for debugging
    console.log('App: Current location', window.location.href);

    // Check if we're on the auth callback page already
    if (window.location.pathname === '/auth/callback') {
      console.log('App: Already on callback page, skipping redirect');
      return;
    }

    // Check for PKCE flow parameters (code in query string)
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (code) {
      console.log('App: Detected auth code, redirecting to callback');
      const callbackUrl = `/auth/callback${window.location.search}`;
      window.location.href = callbackUrl;
      return;
    }

    // Check for hash params (implicit flow)
    // BUT: Skip if this is our custom LinkedIn unified flow (has type=linkedin_unified marker)
    // Those should be handled by their own callback page at /auth/linkedin-unified-callback
    const hash = window.location.hash;
    if (
      hash &&
      hash.includes('access_token=') &&
      !hash.includes('type=linkedin_unified')
    ) {
      console.log('App: Detected auth token in hash, redirecting to callback');
      const callbackUrl = `/auth/callback${hash}`;
      window.location.href = callbackUrl;
      return;
    }
  }, []); // Empty dependency array ensures this runs only once

  return (
    <QueryClientProvider client={queryClient}>
      <CacheInvalidationInitializer />
      <AuthInitializer />
      <ThemeProvider>
        <AppContextProvider>
          <ModalProvider persistToLocalStorage={true}>
            <CacheErrorBoundary
              queryClient={queryClient}
              enableRetry={true}
              enableCacheReset={true}
            >
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  }
                >
                  <AppRoutes />
                </Suspense>
                <Toaster />
                <SonnerToaster position="top-right" expand={true} richColors />
              </ErrorBoundary>
            </CacheErrorBoundary>
          </ModalProvider>
        </AppContextProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      {/* Vercel Analytics for page views and visitor tracking */}
      <Analytics />
    </QueryClientProvider>
  );
}
